import type { SelectOption } from '@movingimage-evp/mi-ui-component-library';
import type { ChangeEvent } from 'react';

import type { Language } from '../../../../generated/graphql-manager';

import {
  CopyToClipboard,
  Heading,
  InputDescription,
  Note,
  Paragraph,
  Select,
  SelectionInputLayout,
  TextInput,
  Toggle,
  Tooltip,
  notification,
  toast,
} from '@movingimage-evp/mi-ui-component-library';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { useCurrentUser } from 'src/manager/hooks/current-user';
import { MULTILANG_SRT_NOT_AVAILABLE_FOR } from 'src/service/feature-toggle/feature-toggle-configuration';

import {
  IngestProtocol,
  State,
  useGetWebcastLanguagesQuery,
  useUpdateWebcastLanguagesMutation,
} from '../../../../generated/graphql-manager';
import { useAbsoluteRoutes } from '../../../../routes';
import { getAiTranslationKey, getLanguageKey } from '../../../../utils';
import { SetupPageFooter } from '../../../components/setup-page-footer';
import { useGetFeatures } from '../../../hooks/use-get-features';
import { useUserPermissions } from '../../../hooks/user-permissions';

import styles from './language.module.css';
import managerStyles from '../../../manager.module.css';

export function LanguagePage() {
  const { webcastId = '' } = useParams();
  const { lsproId } = useCurrentUser();
  const { t } = useTranslation();
  const routes = useAbsoluteRoutes();
  const { isEventEditingAllowed } = useUserPermissions();
  const { isAiSubtitleFeatureEnabled } = useGetFeatures();

  const { data } = useGetWebcastLanguagesQuery({
    variables: { webcastId, lsproId },
    onCompleted({ webcast, lsproSettings, configuration }) {
      const additionalLangsCodes = lsproSettings?.languages.map((language) => language.code) || [];
      const languagesToUse = Array.from(new Set([...configuration.supportedLanguages, ...additionalLangsCodes]));

      setLanguagesData(
        languagesToUse.map((supportedLanguage) => ({
          language: supportedLanguage,
          title: webcast.contents.find(({ language }) => language === supportedLanguage)?.title || '',
        }))
      );
    },
  });

  const webcast = data?.webcast;
  const allChangesPossible = webcast?.state === State.PRELIVE;
  const supportedSubtitleLanguages = data?.configuration.supportedSubtitleLanguages || [];

  const primaryLanguage = webcast?.primaryLang;
  const aiLiveTranscriptGeneration = webcast?.subtitleSettings?.aiLiveTranscriptGeneration === true;
  const aiLiveTranscriptEditorEnabled = webcast?.subtitleSettings?.aiLiveTranscriptEditorEnabled === true;
  const translationLanguages = webcast?.subtitleSettings?.translationLanguages || [];

  const languages = data?.lsproSettings?.languages || [];
  const additionalLanguages =
    webcast?.additionalLangs.filter((language) => language.code !== webcast.primaryLang.code) || [];

  const [languagesData, setLanguagesData] = useState<{ language: string; title: string }[]>([]);
  const [primaryTitleMissingError, setPrimaryTitleMissingError] = useState(false);

  const [updateWebcastLanguagesMutation] = useUpdateWebcastLanguagesMutation();

  const onTitleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { value, name } = event.target;

    setLanguagesData((state) =>
      state.map((language) => (language.language === name ? { ...language, title: value } : language))
    );
  };

  const saveChanges = (options?: {
    primaryLanguage?: Language;
    additionalLanguages?: Language[];
    aiLiveTranscriptGeneration?: boolean;
    aiLiveTranscriptEditorEnabled?: boolean;
    translationLanguages?: string[];
  }) => {
    const newPrimaryLanguage = options?.primaryLanguage || primaryLanguage;

    if (!newPrimaryLanguage || !primaryLanguage) return;

    const updatedAdditionalLanguages = updateAdditionalLanguages(
      primaryLanguage,
      newPrimaryLanguage,
      options?.additionalLanguages || additionalLanguages
    );
    const updatedLanguagesData = updateLanguagesData(primaryLanguage, newPrimaryLanguage);

    setLanguagesData(updatedLanguagesData);

    const languages = [
      { language: newPrimaryLanguage.code, isPrimary: true },
      ...(updatedAdditionalLanguages || []).map((language) => ({ language: language.code, isPrimary: false })),
    ];

    const contents = updatedLanguagesData?.filter(
      ({ language }) => languages.find((lang) => lang.language === language) !== undefined
    );

    updateWebcastLanguagesMutation({
      variables: {
        input: {
          id: webcastId,
          languages: !allChangesPossible ? undefined : languages,
          subtitleSettings: !allChangesPossible
            ? undefined
            : {
                aiLiveTranscriptGeneration: options?.aiLiveTranscriptGeneration ?? aiLiveTranscriptGeneration,
                aiLiveTranscriptEditorEnabled: options?.aiLiveTranscriptEditorEnabled ?? aiLiveTranscriptEditorEnabled,
                translationLanguages: options?.translationLanguages || translationLanguages,
              },
          contents,
        },
      },
      optimisticResponse: {
        updateWebcast: {
          __typename: 'UpdateWebcastSuccess',
          webcast: {
            __typename: 'Webcast',
            id: webcastId,
            primaryLang: options?.primaryLanguage || primaryLanguage,
            additionalLangs: updatedAdditionalLanguages,
            subtitleSettings: {
              __typename: 'SubtitleSettings',
              aiLiveTranscriptGeneration: options?.aiLiveTranscriptGeneration ?? aiLiveTranscriptGeneration,
              aiLiveTranscriptEditorEnabled: options?.aiLiveTranscriptEditorEnabled ?? aiLiveTranscriptEditorEnabled,
              translationLanguages: options?.translationLanguages || translationLanguages,
            },
            contents,
          },
        },
      },
      update: (cache, { data }) => {
        const response = data?.updateWebcast;
        if (response?.__typename !== 'UpdateWebcastSuccess') return;

        cache.modify({
          id: cache.identify({ __typename: 'Webcast', id: webcastId }),
          fields: {
            primaryLang() {
              return options?.primaryLanguage || primaryLanguage;
            },
            additionalLangs() {
              return updatedAdditionalLanguages;
            },
            subtitleSettings() {
              return !allChangesPossible
                ? undefined
                : {
                    aiLiveTranscriptGeneration: options?.aiLiveTranscriptGeneration ?? aiLiveTranscriptGeneration,
                    aiLiveTranscriptEditorEnabled:
                      options?.aiLiveTranscriptEditorEnabled ?? aiLiveTranscriptEditorEnabled,
                    translationLanguages: options?.translationLanguages || translationLanguages,
                  };
            },
            contents() {
              return contents;
            },
          },
        });
      },
      onCompleted: (data) => {
        if (data?.updateWebcast.__typename === 'UpdateWebcastSuccess') {
          toast(t('manager.webcastSetup.toast.saveSuccess'), 'positive');
        } else {
          notification(t('manager.webcastSetup.toast.saveError'), 'negative');
        }
      },
      onError: () => {
        notification(t('manager.webcastSetup.toast.saveError'), 'negative');
      },
    });
  };

  const getPrimaryLanguageTitle = (primaryLanguage?: Language) => {
    return languagesData?.find((language) => language.language === primaryLanguage?.code)?.title || '';
  };

  const handleOnPrimaryTitleBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    if (primaryLanguage && !getPrimaryLanguageTitle(primaryLanguage)) {
      setPrimaryTitleMissingError(true);
    } else {
      setPrimaryTitleMissingError(false);
      handleOnLanguageTitleBlur(event);
    }
  };

  const handleOnLanguageTitleBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    const { name } = event.target;

    const title = webcast?.contents?.find((language) => language.language === name)?.title || '';
    const updatedTitle = languagesData?.find((language) => language.language === name)?.title || '';

    if (title !== updatedTitle) saveChanges();
  };

  const updateAdditionalLanguages = (
    currentPrimaryLanguage: Language,
    newPrimaryLanguage: Language,
    additionalLanguages: Language[]
  ) => {
    if (!currentPrimaryLanguage) return additionalLanguages;

    const hasSwitchedLanguageBeenChecked = additionalLanguages.some(
      (language) => language.code === newPrimaryLanguage.code
    );

    const updatedAdditionalLanguages = (
      hasSwitchedLanguageBeenChecked ? [...additionalLanguages, currentPrimaryLanguage] : additionalLanguages
    ).filter((language) => language.code !== newPrimaryLanguage.code);

    return updatedAdditionalLanguages;
  };

  const updateLanguagesData = (currentPrimaryLanguage: Language, newPrimaryLanguage: Language) => {
    const currentPrimaryLanguageTitle = getPrimaryLanguageTitle(currentPrimaryLanguage);
    const newPrimaryLanguageTitle = getPrimaryLanguageTitle(newPrimaryLanguage);

    const newSelectedPrimaryLanguageHasNoTitle = newPrimaryLanguageTitle === '';

    const updatedLanguagesData = newSelectedPrimaryLanguageHasNoTitle
      ? languagesData?.map((language) => ({
          ...language,
          title: language.language === newPrimaryLanguage.code ? currentPrimaryLanguageTitle : language.title,
        }))
      : languagesData;

    return updatedLanguagesData;
  };

  const supportedSubtitleLanguagesWithoutEventLanguages = supportedSubtitleLanguages.filter(
    (subtitleLanguage) =>
      subtitleLanguage !== primaryLanguage?.code &&
      !additionalLanguages.some((language) => language.code?.startsWith(subtitleLanguage))
  );

  const primaryLanguageOptions = languages
    .filter((language) => language.__typename === 'StandardLanguage')
    .map((language) => ({
      key: language.code,
      label: t(`manager.webcastSetup.language.supportedLanguages.${getLanguageKey(language.code)}`),
    }));

  const primaryLanguageValue = primaryLanguageOptions.find((option) => option.key === primaryLanguage?.code);
  const primaryLanguageTitle = getPrimaryLanguageTitle(primaryLanguage);

  const onPrimaryLanguageChange = (updatedPrimaryLanguage?: Language) => {
    if (!updatedPrimaryLanguage) return;

    saveChanges({ primaryLanguage: updatedPrimaryLanguage });
  };

  const additionalLanguagesOptions = languages
    .filter((language) => language.code !== primaryLanguage?.code)
    .map((language) => ({
      key: language.code,
      label:
        language.__typename === 'CustomLanguage'
          ? language.name
          : t(`manager.webcastSetup.language.supportedLanguages.${getLanguageKey(language.code)}`),
    }));

  const additionalLanguagesValue = additionalLanguagesOptions.filter((option) =>
    additionalLanguages.some((language) => language.code === option.key)
  );

  const aiSubtitlesOptions = supportedSubtitleLanguagesWithoutEventLanguages
    .map((language) => ({
      key: language,
      label: t(`common.languages.${getAiTranslationKey(language)}`),
    }))
    .sort((a, b) => (a.label > b.label ? 1 : -1));

  const isMultiLanguageEvent = additionalLanguages.length > 0;

  // TODO: Temporary - LSPRO-3511
  const isSrtProtocol = webcast?.streaming?.live?.ingestProtocol === IngestProtocol.SRT;
  const isMultilangSrtDisabled = isSrtProtocol && MULTILANG_SRT_NOT_AVAILABLE_FOR.includes(lsproId);

  const languageSelectionDisabled = !isEventEditingAllowed || webcast?.state !== State.PRELIVE;
  const aiSubtitlesDisabled =
    languageSelectionDisabled || webcast?.subtitleSettings?.aiLiveTranscriptGeneration === undefined;

  if (!primaryLanguage) return null;

  return (
    <main className={managerStyles.main} data-testid="language-page">
      <Heading className={managerStyles.grayText}>{t('manager.webcastSetup.language.title')}</Heading>
      <Paragraph>{t('manager.webcastSetup.language.headings.primaryLanguage')}</Paragraph>

      <div className={styles.wrapper}>
        <InputDescription
          label={t('manager.webcastSetup.language.inputs.primaryLanguage.label')}
          disabled={languageSelectionDisabled}
        >
          <Select
            data-testid="primary-language-select"
            disabled={languageSelectionDisabled}
            optionsIdProperty="key"
            value={primaryLanguageValue}
            options={primaryLanguageOptions}
            onChange={(option: SelectOption) => {
              onPrimaryLanguageChange(languages.find((language) => language.code === option.key));
            }}
          />
        </InputDescription>

        <InputDescription
          label={t('manager.webcastSetup.language.inputs.additionalLanguages.label')}
          disabled={languageSelectionDisabled || isMultilangSrtDisabled}
        >
          <Tooltip
            delay={0}
            hidden={!isMultilangSrtDisabled}
            label={t('manager.webcastSetup.language.inputs.additionalLanguages.disabledInfo.srt')}
          >
            <Select
              data-testid="additional-languages"
              disabled={languageSelectionDisabled || isMultilangSrtDisabled}
              placeholder={t('manager.webcastSetup.language.inputs.additionalLanguages.placeholder')}
              optionsIdProperty="key"
              value={additionalLanguagesValue}
              options={additionalLanguagesOptions}
              onChange={(options) => {
                saveChanges({
                  additionalLanguages: languages.filter((language) =>
                    options.some((option: SelectOption) => option.key === language.code)
                  ),
                });
              }}
            />
          </Tooltip>
        </InputDescription>

        {isMultiLanguageEvent && (
          <Paragraph>{t('manager.webcastSetup.language.headings.additionalLanguages')}</Paragraph>
        )}

        <InputDescription
          inputId="primary-language-title"
          label={
            isMultiLanguageEvent
              ? t('manager.webcastSetup.language.inputs.primaryLanguageTitle.multiLanguageLabel')
              : t('manager.webcastSetup.language.inputs.primaryLanguageTitle.label')
          }
          helpMessage={
            !isMultiLanguageEvent
              ? t('manager.webcastSetup.language.inputs.primaryLanguageTitle.helpMessage')
              : undefined
          }
          disabled={!isEventEditingAllowed}
          errorMessage={
            primaryTitleMissingError
              ? t('manager.webcastSetup.language.inputs.primaryLanguageTitle.primaryTitleMissing')
              : undefined
          }
        >
          <TextInput
            value={primaryLanguageTitle}
            name={primaryLanguage.code}
            disabled={!isEventEditingAllowed}
            onChange={onTitleChange}
            onBlur={handleOnPrimaryTitleBlur}
            data-testid="primary-language-title"
          >
            <CopyToClipboard value={primaryLanguageTitle} notificationPosition="left" />
          </TextInput>
        </InputDescription>

        {languagesData
          .filter(
            ({ language }) =>
              language !== primaryLanguage.code && additionalLanguages.find(({ code }) => code === language)
          )
          .map(({ language, title }) => {
            const foundLanguage = additionalLanguages.find(({ code }) => code === language);
            const customLabel = foundLanguage?.__typename === 'CustomLanguage' ? foundLanguage.name : undefined;

            return (
              <InputDescription
                key={language}
                inputId={`language-title-${language}`}
                label={customLabel || t(`common.languages.${getLanguageKey(language)}`)}
                disabled={!isEventEditingAllowed}
              >
                <TextInput
                  data-testid={`language-title-${language}`}
                  name={language}
                  value={title || ''}
                  disabled={!isEventEditingAllowed}
                  onChange={onTitleChange}
                  onBlur={handleOnLanguageTitleBlur}
                />
              </InputDescription>
            );
          })}

        {isAiSubtitleFeatureEnabled && (
          <div className={styles.wrapper}>
            <SelectionInputLayout
              hintMessage={t('manager.webcastSetup.language.inputs.aiSubtitles.hintMessage')}
              checked={aiLiveTranscriptGeneration}
              disabled={aiSubtitlesDisabled}
              variant="ghost"
              inputElement={
                <Toggle
                  data-testid="ai-subtitles-toggle"
                  checked={aiLiveTranscriptGeneration}
                  disabled={aiSubtitlesDisabled}
                  onChange={() => {
                    saveChanges({ aiLiveTranscriptGeneration: !aiLiveTranscriptGeneration });
                  }}
                >
                  {t('manager.webcastSetup.language.inputs.aiSubtitles.label')}
                </Toggle>
              }
            />

            {aiLiveTranscriptGeneration && (
              <>
                <InputDescription label={t('manager.webcastSetup.language.inputs.aiSubtitlesLanguages.label')}>
                  <Select
                    data-testid="ai-subtitles-languages"
                    disabled={aiSubtitlesDisabled}
                    placeholder={t('manager.webcastSetup.language.inputs.aiSubtitlesLanguages.placeholder')}
                    optionsIdProperty="key"
                    value={translationLanguages.map((language) => ({
                      key: language,
                      label: t(`common.languages.${getAiTranslationKey(language)}`),
                    }))}
                    options={aiSubtitlesOptions}
                    onChange={(options) => {
                      saveChanges({ translationLanguages: options.map((option: SelectOption) => option.key) });
                    }}
                  />
                </InputDescription>

                <SelectionInputLayout
                  hintMessage={t('manager.webcastSetup.language.inputs.aiLiveTranscriptEditor.toggle.hintMessage')}
                  checked={aiLiveTranscriptEditorEnabled}
                  variant="ghost"
                  inputElement={
                    <Toggle
                      data-testid="ai-subtitles-editor-toggle"
                      checked={aiLiveTranscriptEditorEnabled}
                      disabled={aiSubtitlesDisabled}
                      onChange={() => {
                        saveChanges({ aiLiveTranscriptEditorEnabled: !aiLiveTranscriptEditorEnabled });
                      }}
                    >
                      {t('manager.webcastSetup.language.inputs.aiLiveTranscriptEditor.toggle.label')}
                    </Toggle>
                  }
                />

                <Note variant="primary" className={styles.note}>
                  <strong>{t('common.aiSubtitlesDelayInfo.title')}</strong>
                  {t('common.aiSubtitlesDelayInfo.full')}
                </Note>
              </>
            )}
          </div>
        )}
      </div>

      <SetupPageFooter
        nextLabel={t('manager.webcastSetup.language.nextStep.label')}
        route={routes.webcastSetup_eventDescription}
      />
    </main>
  );
}
